import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, UserX, UserCheck, LogOut, Mail } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import DisableAccountDialog from "@/components/user-management/DisableAccountDialog";

/**
 * 149-ar + 149-cv-r1 (PROMPT 16-AUTH-R2-R1) + 149-cw-r150: Aksi keamanan akun.
 *
 *  BOD Lv1 only:
 *    - Buat Password Sementara → generate temp password (returned once via modal)
 *    - Force Logout → invalidate active_session_id (kick semua device)
 *
 *  BOD Lv1 OR canUpdateUser (user_management:update):
 *    - Kirim Link Reset ke Email → same token service + email template dgn
 *      self-service Forgot Password. Password user TIDAK diganti.
 *    - Nonaktifkan / Aktifkan → soft-toggle is_active + kick semua sesi
 *      (149-cw-r150: dulu BOD Lv1 only — sekarang matrix-gated supaya HR/staff
 *      dgn `user_management.update` bisa deactivate akun karyawan resign).
 *
 * Props:
 *  - user (target row)
 *  - currentUser (logged-in admin)
 *  - canUpdateUser (bool) — canonical `user_management:update` dari useAccess
 *  - onTempPassword({ user, temp }) → parent buka modal one-time reveal
 *  - onReload() → reload user list
 */
export default function AdminActionsCell({ user: targetUser, currentUser, canUpdateUser, onTempPassword, onReload }) {
  const [disableDlg, setDisableDlg] = useState(false);
  const [disableBusy, setDisableBusy] = useState(false);
  const isBodLv1 = currentUser?.division === "BOD" && Number(currentUser?.level) === 1;
  const canSendResetLink = isBodLv1 || !!canUpdateUser;
  // 149-cw-r150: canDisable — matrix-gated, sama dgn backend endpoint gate.
  const canDisable = isBodLv1 || !!canUpdateUser;

  // Non-admin dan bukan BOD LV1 → tidak ada aksi apapun
  if (!isBodLv1 && !canSendResetLink && !canDisable) return null;

  if (targetUser.id === currentUser?.id) {
    return <span className="text-[10px] text-slate-400 italic">(diri sendiri)</span>;
  }

  const doReset = async () => {
    if (!window.confirm(
      `Buat Password Sementara untuk ${targetUser.name}:\n\n` +
      `Sistem akan generate password sementara + set flag "wajib ganti saat login berikutnya" + logout semua sesi aktif.\n\n` +
      `Password sementara HANYA muncul SATU KALI di layar ini. Segera share ke user via channel aman.\n\nLanjut?`
    )) return;
    try {
      const { data } = await api.post(`/admin/users/${targetUser.id}/reset-password`);
      onTempPassword?.({ user: targetUser, temp: data.temp_password });
      onReload?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal buat password sementara"); }
  };

  const doSendResetLink = async () => {
    if (!targetUser.email) { toast.error("User tidak memiliki alamat email."); return; }
    if (targetUser.is_active === false) { toast.error("Tidak dapat kirim link reset ke akun yang dinonaktifkan."); return; }
    if (!window.confirm(
      `Kirim Link Reset ke Email\n\n` +
      `Target: ${targetUser.name}\n` +
      `Email:  ${targetUser.email}\n\n` +
      `User akan menerima email berisi link untuk buat password baru sendiri. ` +
      `Link berlaku 30 menit dan hanya bisa dipakai sekali.\n\n` +
      `Password saat ini TIDAK berubah sampai user klik link tersebut.\n\nKirim link ke email di atas?`
    )) return;
    try {
      const { data } = await api.post(`/admin/users/${targetUser.id}/send-reset-link`);
      toast.success(data?.message || `Link reset terkirim ke ${targetUser.email}`);
      onReload?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal kirim link reset"); }
  };

  const doDisable = async () => {
    setDisableDlg(true);
  };
  const submitDisable = async (reason) => {
    setDisableBusy(true);
    try {
      await api.post(`/admin/users/${targetUser.id}/disable`, { reason });
      toast.success(`${targetUser.name} dinonaktifkan · alasan: ${reason}`);
      setDisableDlg(false);
      onReload?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal disable"); }
    finally { setDisableBusy(false); }
  };

  const doEnable = async () => {
    try {
      await api.post(`/admin/users/${targetUser.id}/enable`);
      toast.success(`${targetUser.name} diaktifkan`);
      onReload?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal enable"); }
  };

  const doForceLogout = async () => {
    if (!window.confirm(`Force logout ${targetUser.name} dari semua device?`)) return;
    try {
      await api.post(`/admin/users/${targetUser.id}/force-logout`);
      toast.success(`${targetUser.name} di-force logout`);
      onReload?.();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Gagal force logout"); }
  };

  const btnBase = "inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-semibold uppercase tracking-wider transition-colors";

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`admin-actions-${targetUser.id}`}>
      {isBodLv1 && (
        <button
          type="button"
          onClick={doReset}
          data-testid={`btn-reset-${targetUser.id}`}
          title="Buat Password Sementara — muncul 1x, user wajib ganti saat login berikutnya"
          className={`${btnBase} border-sky-400 bg-sky-50 text-sky-700 hover:border-[#FF5A1F]`}
        ><KeyRound size={10} /> Password Sementara</button>
      )}
      {canSendResetLink && targetUser.is_active !== false && !!targetUser.email && (
        <button
          type="button"
          onClick={doSendResetLink}
          data-testid={`btn-send-reset-link-${targetUser.id}`}
          title={`Kirim link reset ke email ${targetUser.email} — password tidak berubah sampai user klik link`}
          className={`${btnBase} border-indigo-400 bg-indigo-50 text-indigo-700 hover:border-[#FF5A1F]`}
        ><Mail size={10} /> Kirim Link Reset</button>
      )}
      {canDisable && (
        targetUser.is_active === false ? (
          <button
            type="button"
            onClick={doEnable}
            data-testid={`btn-enable-${targetUser.id}`}
            title="Aktifkan akun"
            className={`${btnBase} border-emerald-400 bg-emerald-50 text-emerald-700 hover:border-[#FF5A1F]`}
          ><UserCheck size={10} /> Aktifkan</button>
        ) : (
          <button
            type="button"
            onClick={doDisable}
            data-testid={`btn-disable-${targetUser.id}`}
            title="Nonaktifkan akun (karyawan resign, cuti panjang, dll.)"
            className={`${btnBase} border-rose-400 bg-rose-50 text-rose-700 hover:border-[#FF5A1F]`}
          ><UserX size={10} /> Nonaktifkan</button>
        )
      )}
      {isBodLv1 && (
        <button
          type="button"
          onClick={doForceLogout}
          data-testid={`btn-forcelogout-${targetUser.id}`}
          title="Kick semua sesi aktif"
          className={`${btnBase} border-slate-400 bg-slate-50 text-slate-700 hover:border-[#FF5A1F]`}
        ><LogOut size={10} /> Kick</button>
      )}
      <DisableAccountDialog
        open={disableDlg}
        onOpenChange={setDisableDlg}
        userName={targetUser?.name}
        busy={disableBusy}
        onConfirm={submitDisable}
      />
    </div>
  );
}
